/**
 * How to play, and what everything on the screen means.
 */

import { h, ICON, svg } from '../dom';
import { GRADES, GRADE_ORDER } from '../../game/grade';

export function helpSheet(close: () => void): HTMLElement[] {
  // Demo swatches carry their own ink, even though they show no text —
  // every colour block on screen declares its readable foreground.
  const demoA = h('div', { class: 'onboard__swatch', style: { '--swatch': '#1f6fb2', '--on-swatch': '#ffffff' } });
  const demoB = h('div', { class: 'onboard__swatch', style: { '--swatch': '#e8b23a', '--on-swatch': '#0b0b0e' } });
  const demoMix = h('div', { class: 'onboard__swatch', style: { '--swatch': '#8a9a72', '--on-swatch': '#ffffff' } });

  return [
    h(
      'div',
      { class: 'onboard' },
      h('div', { class: 'onboard__art' }, demoA, h('span', { class: 'onboard__plus', text: '+' }), demoB, h('span', { class: 'onboard__plus', text: '=' }), demoMix),
      h('p', {
        class: 'prose',
        text: 'Every day you get a bench of real artist pigments and one colour to match. Tap two colours to mix them. Mix your way as close as you can to the target, then submit.',
      }),
      h('div', { class: 'rule' }),
      step('1', 'Tap a colour', 'It lifts out of the bench.'),
      step('2', 'Tap a second colour', 'They blend. The new colour lands at the end of the bench, and you can blend it again.'),
      step('3', 'Submit', 'Your newest colour is the one that counts. Undo is free and unlimited — your budget, not your patience, is the limit.'),
      h('div', { class: 'rule' }),
      h('h2', { class: 'caps', text: 'Grading' }),
      h(
        'div',
        { class: 'legend' },
        ...GRADE_ORDER.map((g) =>
          h(
            'div',
            { class: 'legend__row', style: { '--grade': `var(--${GRADES[g].token})` } },
            h('span', { class: 'legend__sym', text: GRADES[g].symbol }),
            h('span', { class: 'legend__k', text: GRADES[g].label }),
            h('span', { class: 'legend__d', text: GRADES[g].blurb }),
          ),
        ),
      ),
      h('p', { class: 'prose', text: 'A Miss is the only thing that breaks a streak. Everything else keeps it alive.' }),
      h('div', { class: 'rule' }),
      h('h2', { class: 'caps', text: 'Good to know' }),
      h(
        'ul',
        { class: 'prose' },
        h('li', { text: 'The same puzzle runs for everyone in the world, and it never changes once it is live.' }),
        h('li', { text: 'Miss a day and the streak resets. Close the game early and it survives.' }),
        h('li', { text: 'Past puzzles can be replayed from the archive, for pride only.' }),
        h('li', { text: 'Nothing is uploaded. Your streak lives on this device.' }),
      ),
      h(
        'button',
        {
          class: 'btn btn--wide',
          text: 'Got it',
          attrs: { type: 'button' },
          on: { click: close },
        },
      ),
    ),
  ];
}

function step(n: string, title: string, body: string): HTMLElement {
  return h(
    'div',
    { class: 'onboard__step' },
    h('span', { class: 'onboard__n', text: n, attrs: { 'aria-hidden': 'true' } }),
    h(
      'div',
      { class: 'prose' },
      h('strong', { text: title }),
      h('span', { text: body }),
    ),
  );
}

/** Small info button, for screens that want one inline. */
export function helpButton(): HTMLElement {
  return h(
    'button',
    { class: 'icon', attrs: { type: 'button', 'aria-label': 'How to play' } },
    svg(ICON.help),
  );
}
